import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as invoiceService from '../services/invoiceService';
import { getCompanyInfo } from "../../../services/authService";
import {
  Box, Button, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Divider, Chip, Container, Alert, AlertTitle
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from "react-i18next";

const InvoiceViewPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch invoice
        const invoiceData = await invoiceService.getInvoiceById(id);
        setInvoice(invoiceData?.payload || invoiceData);

        // Fetch company info
        const company = await getCompanyInfo();
        if (company) {
          setCompanyInfo({
            name: company.CompanyName || '',
            vat: company.BTWNumber || '',
            email: company.Email || '',
            phone: company.PhoneNumber || '',
            address: company.Address || '',
            city: company.City || '',
            postCode: company.PostCode || '',
            country: company.Country || '',
            iban: company.IBANumber || '',
          });
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setInvoice(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography>Loading...</Typography>
    </Container>
  );

  if (!invoice) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="error">
        <AlertTitle>Not Found</AlertTitle>
        Invoice not found
      </Alert>
      <Button onClick={() => navigate('/invoices')} sx={{ mt: 2 }}>
        Back to Invoices
      </Button>
    </Container>
  );

  // Extract customer data
  const customer = invoice.Customer || {};
  const customerName = customer.Name || invoice.CustomerName || 'N/A';
  const customerVAT = customer.VATNumber || invoice.CustomerVAT || 'N/A';
  const customerEmail = customer.Email || invoice.CustomerEmail || 'N/A';
  const customerPhone = customer.PhoneNumber || customer.MobilePhone || invoice.CustomerPhone || 'N/A';
  const customerAddress = customer.Street || invoice.CustomerAddress || 'N/A';
  const customerCity = customer.City || invoice.CustomerCity || '';
  const customerPostCode = customer.PostCode || '';
  const customerCountry = customer.Country || '';

  // Calculate totals from line items
  const lineItems = invoice.LineItems || [];
  const totalExcl = lineItems.reduce((sum, item) => sum + Number(item.TotalExcl || 0), 0);
  const vatPercentage = Number(invoice.VATPercentage || 21);
  const totalVAT = (totalExcl * vatPercentage) / 100;
  const totalIncl = totalExcl + totalVAT;

  const paidAmount = Number(invoice.InvoicePaidAmount || 0);
  const dueAmount = totalIncl - paidAmount;

  // Payment status
  let paymentStatus = 'Not Paid';
  let paymentColor = 'error';
  if (paidAmount >= totalIncl) {
    paymentStatus = 'Paid';
    paymentColor = 'success';
  } else if (paidAmount > 0) {
    paymentStatus = 'Partially Paid';
    paymentColor = 'warning';
  }

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const invoiceDate = lineItems.length > 0 && lineItems[0].CreatedDate 
    ? lineItems[0].CreatedDate 
    : invoice.InvoiceDate || invoice.CreatedDate;

  const handleEdit = () => {
    navigate(`/invoices/edit/${invoice.Id}`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await invoiceService.deleteInvoice(invoice.Id);
        navigate('/invoices');
      } catch (error) {
        alert('Failed to delete invoice');
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Invoice #{invoice.InvoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Created: {formatDate(invoiceDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/invoices')}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{ borderRadius: 2 }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              sx={{ borderRadius: 2 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Payment Status Alert */}
      {paymentStatus === 'Paid' && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>✓ Fully Paid</AlertTitle>
          This invoice has been fully paid.
        </Alert>
      )}

      {paymentStatus === 'Partially Paid' && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>⚠ Partially Paid</AlertTitle>
          This invoice has been partially paid. Due amount: €{dueAmount.toFixed(2)}
        </Alert>
      )}

      {paymentStatus === 'Not Paid' && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>✗ Not Paid</AlertTitle>
          This invoice has not been paid yet. Total due: €{dueAmount.toFixed(2)}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                Company Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{companyInfo?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">VAT Number</Typography>
                <Typography variant="body1">{companyInfo?.vat || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{companyInfo?.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{companyInfo?.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {companyInfo?.address || 'N/A'}
                  {companyInfo?.postCode && `, ${companyInfo.postCode}`}
                  {companyInfo?.city && ` ${companyInfo.city}`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">IBAN</Typography>
                <Typography variant="body1">{companyInfo?.iban || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Customer Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                Customer Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{customerName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">VAT Number</Typography>
                <Typography variant="body1">{customerVAT}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{customerEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{customerPhone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {customerAddress}
                  {customerPostCode && `, ${customerPostCode}`}
                  {customerCity && ` ${customerCity}`}
                  {customerCountry && `, ${customerCountry}`}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Invoice Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Invoice Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
                <Typography variant="body1" fontWeight={500}>{invoice.InvoiceNumber}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Payment Status</Typography>
                <Chip
                  label={paymentStatus}
                  color={paymentColor}
                  sx={{ mt: 1, fontWeight: 600, fontSize: '0.875rem' }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Invoice Date</Typography>
                <Typography variant="body1">{formatDate(invoiceDate)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
                <Typography variant="body1">{formatDate(invoice.DueDate)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Subtotal (Excl. VAT)</Typography>
                <Typography variant="h6" fontWeight={600}>€{totalExcl.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">VAT ({vatPercentage}%)</Typography>
                <Typography variant="h6" fontWeight={600}>€{totalVAT.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Total (Incl. VAT)</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#667eea' }}>
                  €{totalIncl.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Paid Amount</Typography>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#4caf50' }}>
                  €{paidAmount.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Due Amount</Typography>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#f44336' }}>
                  €{dueAmount.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Line Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>SKU/ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">VAT %</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total (Excl.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length > 0 ? (
                    lineItems.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography fontWeight={500}>{item.ProductName || item.Name || 'N/A'}</Typography>
                          {item.Description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.Description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.Reference || item.ProductId ? (
                            <Chip 
                              label={item.Reference || item.ProductId} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={item.Quantity || 0} 
                            size="small" 
                            color="info"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={500}>€{Number(item.UnitPrice || item.Price || 0).toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${item.VAT || vatPercentage}%`} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>
                          €{Number(item.TotalExcl || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: '#888', py: 4 }}>
                        No line items found
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Totals Row */}
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      Subtotal (Excl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{totalExcl.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      VAT ({vatPercentage}%):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{totalVAT.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Total (Incl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#667eea' }}>
                      €{totalIncl.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Peppol Information if sent */}
        {invoice.IsSendOnPeppol && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                Peppol Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={invoice.PeppolStatus || 'Not Sent'} 
                    color={invoice.PeppolStatus === 'Sent' ? 'success' : 'default'}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Message ID</Typography>
                  <Typography variant="body2">{invoice.PeppolMessageId || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Sent Date</Typography>
                  <Typography variant="body2">{invoice.PeppolSentDate ? formatDate(invoice.PeppolSentDate) : 'N/A'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Custom Notes if available */}
        {invoice.CustomText && invoice.CustomText.trim() !== '' && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                Additional Notes
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {invoice.CustomText}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default InvoiceViewPage;